import React, { useState, useEffect } from 'react';
import { Search, FileText } from 'lucide-react';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../config';

const getAdminId = () => {
  return Cookies.get('adminId') || '6a6adad6068ce446beb73a07';
};

const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const AppUsageReport = () => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({ employees: [], apps: [], webs: [], matrix: {} });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [groupBy, setGroupBy] = useState('employee');
  const [dateFilterMode, setDateFilterMode] = useState('single');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [rawLogs, setRawLogs] = useState([]);
  const [userMapping, setUserMapping] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const adminId = getAdminId();

        const devicesRes = await fetch(`${API_BASE_URL}/devices?adminId=${adminId}`);
        const devices = await devicesRes.json();
        const mapping = {};
        if (Array.isArray(devices)) {
          devices.forEach(d => {
            if (d.computerName) {
              mapping[d.computerName.toLowerCase()] = d.assignedUser || d.computerName;
            }
          });
        }
        setUserMapping(mapping);

        const logsRes = await fetch(`${API_BASE_URL}/app-usage?adminId=${adminId}`);
        const { websites = [], applications = [] } = await logsRes.json();

        setRawLogs([...websites, ...applications]);
      } catch (err) {
        console.error('Failed to load report data', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;

    const rawMatrix = {};
    const appSet = new Set();
    const webSet = new Set();
    const employeeSet = new Set();

    rawLogs.forEach(log => {
      if (!log.startTime) return;

      if (dateFilterMode === 'single' && selectedDate) {
        const logDate = new Date(log.startTime).toISOString().split('T')[0];
        if (logDate !== selectedDate) return;
      } else if (dateFilterMode === 'range' && startDate && endDate) {
        const logTime = new Date(log.startTime).getTime();
        const startLimit = new Date(startDate).setHours(0, 0, 0, 0);
        const endLimit = new Date(endDate).setHours(23, 59, 59, 999);
        if (logTime < startLimit || logTime > endLimit) return;
      }

      const start = new Date(log.startTime).getTime();
      const end = log.endTime ? new Date(log.endTime).getTime() : Date.now();
      const duration = Math.max(0, end - start);

      let appName = 'Unknown';
      let type = 'app';
      if (log.url) {
        type = 'web';
        try {
          const domain = new URL(log.url.startsWith('http') ? log.url : `https://${log.url}`).hostname;
          appName = domain;
        } catch(e) {
          appName = log.url;
        }
      } else if (log.appName) {
        appName = log.appName;
      }

      const compNameStr = log.computerName ? log.computerName.toLowerCase() : 'unknown';
      const employeeName = userMapping[compNameStr] || log.computerName || 'Unknown Employee';

      if (type === 'web') {
        webSet.add(appName);
      } else {
        appSet.add(appName);
      }
      employeeSet.add(employeeName);

      if (!rawMatrix[employeeName]) {
        rawMatrix[employeeName] = {};
      }
      if (!rawMatrix[employeeName][appName]) {
        rawMatrix[employeeName][appName] = { duration: 0 };
      }
      rawMatrix[employeeName][appName].duration += duration;
    });

    const flatMatrix = {};
    
    Object.keys(rawMatrix).forEach(emp => {
      flatMatrix[emp] = {};
      Object.keys(rawMatrix[emp]).forEach(app => {
        flatMatrix[emp][app] = rawMatrix[emp][app].duration;
      });
    });

    setReportData({
      employees: Array.from(employeeSet).sort(),
      apps: Array.from(appSet).sort(),
      webs: Array.from(webSet).sort(),
      matrix: flatMatrix
    });
  }, [rawLogs, userMapping, selectedDate, startDate, endDate, dateFilterMode, loading]);

  const filteredEmployees = reportData.employees.filter(emp =>
    groupBy === 'employee' ? emp.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const baseColumns = filterType === 'all'
    ? [...reportData.apps, ...reportData.webs].sort()
    : filterType === 'app'
      ? reportData.apps
      : reportData.webs;

  const columnsToShow = groupBy === 'app'
    ? baseColumns.filter(col => col.toLowerCase().includes(searchQuery.toLowerCase()))
    : baseColumns;

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{
            background: 'rgba(var(--app-primary-color-rgb), 0.12)',
            border: '1px solid rgba(var(--app-primary-color-rgb), 0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '14px',
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <FileText size={28} color="var(--accent-primary)" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h1 style={{ margin: '0 0 6px 0', fontSize: 28, fontWeight: 800, color: 'var(--text-primary)' }}>App &amp; Website Usage</h1>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', letterSpacing: '0.2px' }}>Cross-tab analysis of time spent on applications and websites by employees.</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 10, overflow: 'hidden', backdropFilter: 'blur(10px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <button
              onClick={() => setFilterType('all')}
              style={{ padding: '10px 18px', background: filterType === 'all' ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent', border: 'none', color: filterType === 'all' ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', boxShadow: filterType === 'all' ? 'inset 0 -2px 0 var(--accent-primary)' : 'none' }}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('app')}
              style={{ padding: '10px 18px', background: filterType === 'app' ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent', border: 'none', color: filterType === 'app' ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', borderLeft: '1px solid var(--border-light)', borderRight: '1px solid var(--border-light)', boxShadow: filterType === 'app' ? 'inset 0 -2px 0 var(--accent-primary)' : 'none' }}
            >
              Apps Only
            </button>
            <button
              onClick={() => setFilterType('web')}
              style={{ padding: '10px 18px', background: filterType === 'web' ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent', border: 'none', color: filterType === 'web' ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', boxShadow: filterType === 'web' ? 'inset 0 -2px 0 var(--accent-primary)' : 'none' }}
            >
              Websites Only
            </button>
          </div>

          <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: 10, overflow: 'hidden', backdropFilter: 'blur(10px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <button
              onClick={() => setGroupBy('employee')}
              style={{ padding: '10px 18px', background: groupBy === 'employee' ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent', border: 'none', color: groupBy === 'employee' ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', borderRight: '1px solid var(--border-light)', boxShadow: groupBy === 'employee' ? 'inset 0 -2px 0 var(--accent-primary)' : 'none' }}
            >
              By Employee
            </button>
            <button
              onClick={() => setGroupBy('app')}
              style={{ padding: '10px 18px', background: groupBy === 'app' ? 'rgba(var(--app-primary-color-rgb), 0.15)' : 'transparent', border: 'none', color: groupBy === 'app' ? 'var(--accent-primary)' : 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', boxShadow: groupBy === 'app' ? 'inset 0 -2px 0 var(--accent-primary)' : 'none' }}
            >
              By App / Web
            </button>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
            borderRadius: 10,
            padding: '8px 14px',
            gap: 12,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <select
              value={dateFilterMode}
              onChange={e => setDateFilterMode(e.target.value)}
              style={{ background: 'transparent', color: 'var(--text-primary)', border: 'none', outline: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
            >
              <option value="all" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>All Time</option>
              <option value="single" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Single Date</option>
              <option value="range" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Date Range</option>
            </select>

            {dateFilterMode === 'single' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  background: 'transparent', border: 'none', color: 'var(--accent-primary)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', fontSize: 13, fontWeight: 600
                }}
              />
            )}

            {dateFilterMode === 'range' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                />
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                />
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-light)',
            borderRadius: 10,
            padding: '10px 16px',
            width: '260px',
            gap: 12,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <Search size={18} color="var(--text-muted)" />
            <input
              type="text"
              placeholder={groupBy === 'employee' ? "Search Employee..." : "Search App / Web..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                outline: 'none',
                width: '100%',
                fontSize: 14
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="dash-glass-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 16, padding: 0, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid var(--border-light)' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--accent-primary)', fontWeight: 600, letterSpacing: '0.5px' }}>
            <div style={{ width: 24, height: 24, border: '3px solid rgba(var(--app-primary-color-rgb), 0.3)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: 12 }}></div>
            Aggregating usage data...
          </div>
        ) : (
          <div style={{
            overflow: 'auto',
            flex: 1,
            background: 'var(--bg-card)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 'max-content' }}>
              <thead>
                <tr>
                  <th style={{
                    padding: '20px 24px',
                    color: 'var(--accent-primary)',
                    fontSize: 13,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--border-light)',
                    borderRight: '1px solid var(--border-light)',
                    background: 'var(--bg-card)',
                    backdropFilter: 'blur(10px)',
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    zIndex: 10,
                    boxShadow: '2px 0 10px rgba(0,0,0,0.1)'
                  }}>
                    {groupBy === 'employee' ? 'EMPLOYEE NAME' : 'APP / WEBSITE'}
                  </th>
                  {(groupBy === 'employee' ? columnsToShow : filteredEmployees).map(headerCol => (
                    <th key={headerCol} title={headerCol} style={{
                      padding: '20px 24px',
                      color: 'var(--text-primary)',
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: '0.3px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg-card)',
                      backdropFilter: 'blur(10px)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      whiteSpace: 'nowrap',
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {headerCol}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(groupBy === 'employee' ? filteredEmployees : columnsToShow).length === 0 ? (
                  <tr>
                    <td colSpan={(groupBy === 'employee' ? columnsToShow : filteredEmployees).length + 1} style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 16 }}>
                      No usage data found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  (groupBy === 'employee' ? filteredEmployees : columnsToShow).map((rowItem, i) => {
                    const rowBg = i % 2 === 0 ? 'var(--bg-card)' : 'var(--input-bg)';
                    return (
                      <tr key={rowItem} style={{
                        borderBottom: '1px solid var(--border-light)',
                        background: rowBg,
                        transition: 'background 0.2s',
                      }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--app-primary-color-rgb), 0.12)'}
                        onMouseOut={e => e.currentTarget.style.background = rowBg}
                      >
                        <td title={rowItem} style={{
                          padding: '18px 24px',
                          color: 'var(--text-primary)',
                          fontSize: 14,
                          fontWeight: 600,
                          borderRight: '1px solid var(--border-light)',
                          background: rowBg,
                          backdropFilter: 'blur(5px)',
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          whiteSpace: 'nowrap',
                          maxWidth: '300px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          boxShadow: '2px 0 10px rgba(0,0,0,0.05)'
                        }}>
                          {rowItem}
                        </td>
                        {(groupBy === 'employee' ? columnsToShow : filteredEmployees).map(colItem => {
                          const emp = groupBy === 'employee' ? rowItem : colItem;
                          const col = groupBy === 'employee' ? colItem : rowItem;
                          const val = reportData.matrix[emp]?.[col] || 0;
                          return (
                            <td key={colItem} style={{
                              padding: '18px 24px',
                              color: val > 0 ? 'var(--text-primary)' : 'var(--text-muted)',
                              fontSize: 14,
                              fontWeight: val > 0 ? 700 : 400,
                              whiteSpace: 'nowrap',
                              background: val > 0 ? 'rgba(var(--app-primary-color-rgb), 0.18)' : 'transparent'
                            }}>
                              {val > 0 ? formatDuration(val) : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
              {(groupBy === 'employee' ? filteredEmployees : columnsToShow).length > 0 && (
                <tfoot>
                  <tr style={{ background: 'rgba(var(--app-primary-color-rgb), 0.1)', borderTop: '2px solid rgba(var(--app-primary-color-rgb), 0.4)' }}>
                    <td style={{
                      padding: '20px 24px',
                      color: 'var(--accent-primary)',
                      fontSize: 14,
                      fontWeight: 800,
                      letterSpacing: '0.5px',
                      borderRight: '1px solid var(--border-light)',
                      background: 'var(--bg-card)',
                      backdropFilter: 'blur(10px)',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      whiteSpace: 'nowrap',
                      boxShadow: '2px -2px 10px rgba(0,0,0,0.1)'
                    }}>
                      TOTAL USAGE
                    </td>
                    {(groupBy === 'employee' ? columnsToShow : filteredEmployees).map(headerCol => {
                      const colTotal = (groupBy === 'employee' ? filteredEmployees : columnsToShow).reduce((sum, rowItem) => {
                        const emp = groupBy === 'employee' ? rowItem : headerCol;
                        const col = groupBy === 'employee' ? headerCol : rowItem;
                        return sum + (reportData.matrix[emp]?.[col] || 0);
                      }, 0);
                      return (
                        <td key={headerCol} style={{
                          padding: '20px 24px',
                          color: colTotal > 0 ? 'var(--accent-primary)' : 'var(--text-muted)',
                          fontSize: 14,
                          fontWeight: 800,
                          whiteSpace: 'nowrap'
                        }}>
                          {colTotal > 0 ? formatDuration(colTotal) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppUsageReport;
